import React, { FC, useCallback, useMemo } from 'react';
import { Button, Modal, Table } from 'src/common';
import { useTranslation, Trans } from 'react-i18next';
import { ColumnProps } from 'antd/lib/table';
import styles from '../index.module.scss';
import {
  DEFAULT_KEYWORD_SETTING,
  FilterLogic,
  SearchType,
  HighlightColor,
  IKeyword,
  SELECTABLE_LOGIC
} from 'src/models/matchingProfiles/types';
import DeleteButton from '../DeleteButton';
import SelectField from '../SelectField';
import KeywordsArea from '../../KeywordsArea';
import { checkIfOptionIsEnabled } from '../../helpers';
import InfoLabel from 'src/shared/InfoLabel';
import { useFeatureFlag, FeatureFlag } from 'src/helpers/featureFlag';
import { trackMPEditAddKeywords } from 'src/segment/events';
import { usePrefixSearchLimit } from 'src/models/matchingProfiles/hooks';
import { SearchLogic } from '@tendium/prom-types';

interface IProps {
  onClose: () => void;
  disabled?: boolean;
  keywords: IKeyword[];
  mpId: string;
  mpName: string;
}

function useTableColumns(isPrefixSearchLimit: boolean, disabled?: boolean, mpId?: string): ColumnProps<IKeyword>[] {
  const { t } = useTranslation();
  const MPBroadOrNarrowSearchFlag = useFeatureFlag(FeatureFlag.MatchingProfile_BroadOrNarrowSearchInDocuments);
  const MPHighlightSearchFlag = useFeatureFlag(FeatureFlag.MatchingProfile_HighlightSearchInDocuments);
  const prefixSearchLimit = usePrefixSearchLimit();

  return useMemo((): ColumnProps<IKeyword>[] => {
    return [
      {
        title: t('Tenders.keyword'),
        key: 'value',
        dataIndex: 'value',
        className: styles.colKeyword,
        render: value => <span className={styles.keyword}>{value}</span>
      },
      {
        title: <InfoLabel title={t('Tenders.whereToSearch')} desc={<Trans i18nKey={'Tenders.whereToSearchDesc'} />} />,
        key: 'searchType',
        dataIndex: 'searchType',
        className: styles.colSearchTypeSetting,
        render: (searchType: SearchType, record) => (
          <SelectField
            values={Object.values(SearchType).map(type => ({
              value: type,
              label: t(`Tenders.SearchType.${type}`),
              disabled: !checkIfOptionIsEnabled(
                record.filterLogic,
                type,
                MPBroadOrNarrowSearchFlag,
                MPHighlightSearchFlag
              )
            }))}
            className={styles.searchTypeSelect}
            selectedValue={searchType || DEFAULT_KEYWORD_SETTING.searchType}
            label={t('Tenders.whereToSearch')}
            field={'searchType'}
            keyword={record}
            disabled={disabled}
          />
        )
      },
      {
        title: <InfoLabel title={t('Tenders.filterLogic')} desc={<Trans i18nKey={'Tenders.filterLogicDesc'} />} />,
        key: 'filterLogic',
        dataIndex: 'filterLogic',
        className: styles.colSetting,
        render: (filterLogic: FilterLogic, record) => (
          <SelectField
            values={Object.values(FilterLogic).map(logicType => ({
              value: logicType,
              label: t(`Tenders.FilterLogic.${logicType}`),
              disabled: !checkIfOptionIsEnabled(
                logicType,
                record.searchType,
                MPBroadOrNarrowSearchFlag,
                MPHighlightSearchFlag
              )
            }))}
            selectedValue={filterLogic || DEFAULT_KEYWORD_SETTING.filterLogic}
            label={t('Tenders.filterLogic')}
            field={'filterLogic'}
            keyword={record}
            disabled={disabled}
          />
        )
      },
      {
        title: <InfoLabel title={t('Tenders.searchLogic')} desc={<Trans i18nKey={'Tenders.searchLogicDesc'} />} />,
        key: 'searchLogic',
        dataIndex: 'searchLogic',
        className: styles.colSetting,
        render: (searchLogic: SearchLogic, record) => (
          <SelectField
            values={SELECTABLE_LOGIC.map(el => ({
              value: el,
              label: t(`Tenders.SearchLogic.${el}`),
              disabled: isPrefixSearchLimit && el === SearchLogic.PrefixMatch,
              tooltip:
                isPrefixSearchLimit && el === SearchLogic.PrefixMatch
                  ? t('Tenders.prefixSearchLimitReached', { keywords: prefixSearchLimit })
                  : undefined
            }))}
            selectedValue={searchLogic || DEFAULT_KEYWORD_SETTING.searchLogic}
            label={t('Tenders.searchLogic')}
            field={'searchLogic'}
            keyword={record}
            disabled={disabled}
          />
        )
      },
      {
        title: (
          <InfoLabel title={t('Tenders.highlightColour')} desc={<Trans i18nKey={'Tenders.highlightsColourDesc'} />} />
        ),
        key: 'highlightColor',
        dataIndex: 'highlightColor',
        className: styles.colAction,
        render: (highlightColor: HighlightColor, record) => (
          <>
            <SelectField
              values={Object.keys(HighlightColor).map(el => ({ value: el, label: t(`Tenders.HighlightColor.${el}`) }))}
              selectedValue={highlightColor || DEFAULT_KEYWORD_SETTING.highlightColor}
              label={t('Tenders.highlightColour')}
              field={'highlightColor'}
              keyword={record}
              disabled={disabled}
            />
            {!disabled && mpId && <DeleteButton keywordId={record.id} mpId={mpId} />}
          </>
        )
      }
    ];
  }, [MPBroadOrNarrowSearchFlag, MPHighlightSearchFlag, disabled, isPrefixSearchLimit, mpId, prefixSearchLimit, t]);
}

export const AdvancedSettingsModal: FC<IProps> = props => {
  const prefixSearchLimit = usePrefixSearchLimit();
  const { onClose, disabled, mpId, keywords, mpName } = props;
  const { t } = useTranslation();

  const isPrefixSearchLimit = useMemo(
    () => keywords.filter(keyword => keyword.searchLogic === SearchLogic.PrefixMatch).length >= prefixSearchLimit,
    [keywords, prefixSearchLimit]
  );
  const columns = useTableColumns(isPrefixSearchLimit, disabled, mpId);

  const onPressEnter = useCallback(() => {
    trackMPEditAddKeywords({ id: mpId, name: mpName });
  }, [mpId, mpName]);

  return (
    <Modal
      title={t('Tenders.advancedKeywordSettings')}
      visible
      onCancel={onClose}
      width={890}
      className={styles.modal}
      footer={
        <Button type={'primary'} loading={false} onClick={onClose}>
          {t('Common.done')}
        </Button>
      }
    >
      <Table className={styles.table} columns={columns} dataSource={keywords} rowKey={'id'} />
      <div className={styles.footer}>
        <KeywordsArea
          keywords={keywords}
          disabled={!!disabled}
          extendedVersion
          mpId={mpId}
          mpName={mpName}
          onPressEnter={onPressEnter}
        />
      </div>
    </Modal>
  );
};

export default AdvancedSettingsModal;
